import {
  DEFAULT_SESSION_CHANNEL_NAME,
  SessionProvider as SharedSessionProvider,
  type SessionClientApi,
} from '@dawn/session-web';
import { useCallback, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConfigQuery } from '@/hooks/queries/use-config-query';
import { authApi, sessionApi, type User } from '@/lib/api';
import { useSessionStore } from '@/stores/session';

export const SESSION_CHANNEL_NAME = DEFAULT_SESSION_CHANNEL_NAME;

// Module-level (referentially stable) adapters over the dashboard API client —
// the shared provider re-subscribes when the api object's identity changes.
const sessionClientApi: SessionClientApi<User> = {
  me: async () => {
    const data = await authApi.me();
    return data
      ? {
          user: data.user,
          sessionCreatedAt: data.sessionCreatedAt,
          maxAllowedTime: data.maxAllowedTime,
        }
      : null;
  },
  ping: () => sessionApi.ping(),
};

interface SessionProviderProps {
  children: ReactNode;
}

/**
 * Dashboard binding of the shared @dawn/session-web provider (TDEV-073): the
 * session timers, activity ping, warning/expiry state machine and cross-tab
 * sync all live in the package; this wrapper supplies the dashboard's store,
 * API adapters, config-derived timing, and the /login redirect.
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const navigate = useNavigate();
  const configQuery = useConfigQuery();
  const timing = configQuery.data?.timing ?? null;

  // Sign-out lands on /login with the page the user was on, so signing back in
  // resumes it — query included (the MCP OAuth consent page carries the whole
  // authorization request in it). A ref keeps the callback stable.
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const onSignedOut = useCallback(() => {
    // Already on /login (e.g. another tab signs out again): keep the page it
    // will return to rather than replacing it with /login itself.
    if (locationRef.current.pathname === '/login') return;
    navigate('/login', { state: { from: locationRef.current } });
  }, [navigate]);

  return (
    <SharedSessionProvider
      store={useSessionStore}
      timing={timing}
      api={sessionClientApi}
      onSignedOut={onSignedOut}
      channelName={SESSION_CHANNEL_NAME}
    >
      {children}
    </SharedSessionProvider>
  );
}
